import clsx from "clsx";
import { useState } from "react";

import { Button, Icon } from "../";
import { useAlert } from "../Alert/useAlert";
import { IconsType } from "../Icon/utils";

type CopyableFieldProps = {
  appearance?: "default" | "inline" | "link-inline" | "link";
  className?: string;
  icon?: IconsType;
  value: string;
};

export function CopyableField({
  value,
  className,
  appearance = "default",
  icon,
}: CopyableFieldProps) {
  const [copied, setCopied] = useState(false);
  const { showAlertAsync } = useAlert();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showAlertAsync(Promise.resolve(), {
        error: () => ({ message: "", title: "Failed to copy", type: "error" }),
        loading: { message: "", title: "", type: "default" },
        success: { message: "", title: "Copied to clipboard", type: "success" },
      });
    } catch (error) {
      showAlertAsync(Promise.reject(error), {
        error: () => ({ message: "", title: "Failed to copy", type: "error" }),
        loading: { message: "", title: "", type: "default" },
        success: { message: "", title: "", type: "default" },
      }).catch(() => {
        // Error already shown to user via alert
      });
    }
  };

  if (appearance === "inline") {
    return (
      <div className="flex items-center gap-1">
        {icon && <Icon name={icon} className="h-3 w-3 text-gray-400" />}
        <span className="text-xs md:text-sm text-gray-500 font-normal">
          {value}
        </span>
        <Button
          type="button"
          onClick={handleCopy}
          icon={copied ? "circleCheck" : "copy"}
          iconClassName="text-gray-500"
          plain
        />
      </div>
    );
  }

  if (appearance.startsWith("link")) {
    return (
      <div className="relative grid grid-cols-[1fr_auto]">
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-xs md:text-sm text-gray-500 underline font-normal"
        >
          {value}
        </a>
        {appearance === "link-inline" ? (
          <Button
            type="button"
            onClick={handleCopy}
            icon={copied ? "circleCheck" : "copy"}
            iconClassName="text-gray-500"
            plain
          />
        ) : (
          <Button
            type="button"
            onClick={handleCopy}
            className="px-3 text-xs bg-gray-900 text-white hover:bg-gray-800 rounded-md"
          >
            <Icon
              name={copied ? "circleCheck" : "copy"}
              className="mr-1 h-3 w-3"
            />
            {copied ? "Copied!" : "Copy"}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        readOnly
        className={clsx(
          "block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 text-xs md:text-sm",
          "focus:outline-none focus:ring-2 focus:ring-opacity-50",
          "disabled:cursor-not-allowed bg-gray-50 disabled:text-gray-500",
          "pr-24",
          className,
        )}
      />
      <Button
        type="button"
        onClick={handleCopy}
        className="absolute right-0 top-0 h-full px-3 text-xs bg-gray-900 text-white hover:bg-gray-800 rounded-none rounded-r-md"
      >
        <Icon name={copied ? "circleCheck" : "copy"} className="mr-1 h-3 w-3" />
        {copied ? "Copied!" : "Copy"}
      </Button>
    </div>
  );
}
